import type { Authentication } from '../generated/models/authentication';
import type { LogoutResponse as RawLogoutResponse } from '../generated/models/logout-response';
import type { PsRegistration } from '../generated/models/ps-registration';
import { extractStatusMessage, unwrapData } from './adapter.utils';

import type {
  AuthenticationResponse,
  AuthToken,
  AuthUser,
  JwtPayload,
  LogoutResponse,
  PasswordResetResponse,
  PsRegistrationData,
  RegisteringTroubleResponse,
} from '../../models/auth-token.model';

/**
 * Builds the login request body for `POST /auth/token`.
 * Credentials are supplied separately via Basic Auth headers.
 *
 * @param role - User role; always `'ps'` for process servers.
 */
export function toPayload(role: string): Authentication {
  return { role };
}

/**
 * Builds the request payload for `POST /auth/refresh`.
 *
 * @remarks
 * The refresh endpoint requires no body — the server identifies the caller
 * from the `Authorization: Bearer <token>` header only. Returns an empty
 * object to satisfy the adapter convention that every POST has a `toPayload`.
 */
export function refreshToPayload(): Record<string, never> {
  return {};
}

/**
 * Builds the request payload for `POST /auth/logout`.
 *
 * @remarks
 * The logout endpoint requires no body — the server identifies the caller
 * from the `Authorization: Bearer <token>` header only. Returns an empty
 * object to satisfy the adapter convention that every POST has a `toPayload`.
 */
export function logoutToPayload(): Record<string, never> {
  return {};
}

/**
 * Maps the raw API authentication response (snake_case) to the
 * client-side {@link AuthToken} (camelCase) and calculates `expiresAt`.
 *
 * @param raw - Response from `POST /auth/token` or `POST /auth/refresh`.
 */
export function toModel(raw: AuthenticationResponse): AuthToken {
  return {
    accessToken: raw.access_token,
    tokenType: raw.token_type,
    expiresIn: raw.expires_in,
    expiresAt: Date.now() + raw.expires_in * 1000,
    ...(raw.refresh_expires_in !== undefined && { refreshExpiresIn: raw.refresh_expires_in }),
    user: decodeUserFromJwt(raw.access_token),
  };
}

/**
 * Decodes the JWT payload and maps the `claim` object to {@link AuthUser}.
 * Exported so `AuthService` can reconstruct a user from a stored access token
 * without needing the full API response shape.
 *
 * @param accessToken - Raw JWT string (three base64url segments separated by `.`).
 * @returns Decoded {@link AuthUser} derived from the `claim` block in the payload.
 * @throws {Error} When `accessToken` is not a valid three-segment JWT or the payload
 *   cannot be base64-decoded / JSON-parsed.
 */
export function decodeUserFromJwt(accessToken: string): AuthUser {
  const parts = accessToken?.split('.');
  if (!parts || parts.length !== 3) {
    throw new Error(`[auth.adapter] Invalid JWT: expected 3 segments, got ${parts?.length ?? 0}.`);
  }
  let payload: JwtPayload;
  try {
    payload = JSON.parse(atob(parts[1])) as JwtPayload;
  } catch {
    throw new Error('[auth.adapter] JWT payload could not be decoded — malformed base64 or JSON.');
  }
  const { claim } = payload;
  return {
    userId: claim.user_id,
    entityId: claim.entity_id,
    firmId: claim.firm_id,
    name: claim.name,
    email: claim.email,
    role: claim.role,
    entityType: claim.entity_type,
    initials: buildInitials(claim.name),
  };
}

/**
 * Maps the raw `POST /auth/logout` response to the domain {@link LogoutResponse}.
 *
 * @remarks
 * Live API returns `{ message: "Successfully logged out" }`.
 * The adapter enforces the boundary between the generated wire type and domain model.
 *
 * @param raw - Raw response from `logoutAuthenticatedUser`.
 */
export function logoutToModel(raw: unknown): LogoutResponse {
  const r = raw as RawLogoutResponse;
  return { message: r.message };
}

/**
 * Maps the `POST /auth/register` response to {@link PasswordResetResponse}.
 *
 * @remarks
 * Live API returns `{ status: "success", message: "Registration received, please watch your email..." }`.
 * Same envelope shape as `forgetPasswordToModel`.
 *
 * @param raw - Untyped API response.
 */
export function registerToModel(raw: unknown): PasswordResetResponse {
  return extractStatusMessage(raw);
}

/**
 * Builds the `multipart/form-data` payload for `POST /auth/register`.
 *
 * @param data - Process-server registration form data in camelCase domain shape.
 */
export function registerToPayload(data: PsRegistrationData): PsRegistration {
  return {
    firstname: data.firstName,
    middlename: data.middleName,
    lastname: data.lastName,
    email: data.email,
    password: data.password,
    reenter_password: data.reenterPassword,
    mobilephonenumber: data.mobilePhone,
    address_1: data.address1,
    ...(data.address2 !== undefined && { address_2: data.address2 }),
    city: data.city,
    state: data.state,
    zipcode: data.zipCode,
    ...(data.profilePhoto !== undefined && { profile_photo: data.profilePhoto }),
  };
}

/**
 * Maps the raw `GET /auth/forgetpassword` response to {@link PasswordResetResponse}.
 *
 * @remarks
 * Live API always returns a success envelope to prevent user enumeration:
 * `{ status: "success", message: "If an account exists..." }`.
 *
 * @param raw - Untyped API response.
 */
export function forgetPasswordToModel(raw: unknown): PasswordResetResponse {
  return extractStatusMessage(raw);
}

/**
 * Maps the raw `GET /auth/registeringtrouble` response to
 * {@link RegisteringTroubleResponse}.
 *
 * @remarks
 * Live API shape: `{ data: { call_field_operation: "phone_number" } }`.
 *
 * @param raw - Untyped API response.
 */
export function registeringTroubleToModel(raw: unknown): RegisteringTroubleResponse {
  const data = unwrapData(raw);
  return { callNumber: (data['call_field_operation'] as string) ?? '' };
}

/** Derives a two-letter initials string (e.g. `"Marcus Henderson"` → `"MH"`). */
function buildInitials(name: string): string {
  return name
    .split(' ')
    .map((word) => word[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
}
